import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSubcontractors } from '../api/subcontractors';
import type { SubcontractorDto } from '../types';

interface UseSubcontractorsOptions {
  isActive?: boolean;
}

/**
 * Hook for fetching and managing subcontractors list
 */
export function useSubcontractors(options?: UseSubcontractorsOptions) {
  const [data, setData] = useState<SubcontractorDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize options to avoid unnecessary re-renders
  const optionsKey = useMemo(() => JSON.stringify(options), [options]);

  const fetchSubcontractors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const subcontractors = await getSubcontractors(options?.isActive);
      setData(subcontractors);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch subcontractors');
    } finally {
      setIsLoading(false);
    }
  }, [options?.isActive]);

  useEffect(() => {
    fetchSubcontractors();
  }, [optionsKey, fetchSubcontractors]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSubcontractors,
  };
}
